import { useParams, Link } from 'react-router-dom'
import { Clock, TrendingUp, Users, Globe, Search, Smartphone, ShoppingCart, BarChart3, CheckCircle, ArrowLeft } from 'lucide-react'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useCart, ConsultingPackage } from '../contexts/CartContext'
import { useLanguage } from '../contexts/LanguageContext'
import CartModal from '../components/CartModal'
import CartButton from '../components/CartButton'

const ConsultingDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { addToCart } = useCart()
  const { language } = useLanguage()
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [quantity, setQuantity] = useState(1)

  const consultingPackages: ConsultingPackage[] = [
    {
      id: 'google-ads-consulting',
      name: 'Google Ads Danışmanlığı',
      nameEn: 'Google Ads Consulting',
      description: 'Google Ads kampanyalarınızı optimize edin ve ROI\'nizi artırın. Uzman danışmanlarımızla 1 saatlik özel danışmanlık.',
      descriptionEn: 'Optimize your Google Ads campaigns and increase your ROI. 1-hour private consultation with our experts.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'Kampanya stratejisi belirleme',
        'Anahtar kelime analizi',
        'ROI optimizasyonu',
        'Reklam metni önerileri',
        'Performans raporlama',
        'Hedef kitle optimizasyonu',
        'Reklam grubu yapılandırması',
        'Teklif stratejileri'
      ],
      featuresEn: [
        'Campaign strategy development',
        'Keyword analysis',
        'ROI optimization',
        'Ad copy suggestions',
        'Performance reporting',
        'Audience optimization',
        'Ad group structuring',
        'Bidding strategies'
      ],
      category: 'Reklam Yönetimi',
      categoryEn: 'Ad Management',
      icon: 'TrendingUp'
    },
    {
      id: 'meta-ads-consulting',
      name: 'Meta Ads Danışmanlığı',
      nameEn: 'Meta Ads Consulting',
      description: 'Facebook ve Instagram reklamlarınızı en verimli şekilde yönetin. Sosyal medya reklam stratejileri ve optimizasyon.',
      descriptionEn: 'Manage your Facebook and Instagram ads most efficiently. Social media ad strategies and optimization.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'Hedef kitle analizi',
        'Yaratıcı stratejileri',
        'Bütçe optimizasyonu',
        'Dönüşüm takibi',
        'A/B test önerileri',
        'Retargeting stratejileri',
        'Lookalike audience oluşturma',
        'Video reklam optimizasyonu'
      ],
      featuresEn: [
        'Audience analysis',
        'Creative strategies',
        'Budget optimization',
        'Conversion tracking',
        'A/B test suggestions',
        'Retargeting strategies',
        'Lookalike audience creation',
        'Video ad optimization'
      ],
      category: 'Reklam Yönetimi',
      categoryEn: 'Ad Management',
      icon: 'Users'
    },
    {
      id: 'seo-consulting',
      name: 'SEO Danışmanlığı',
      nameEn: 'SEO Consulting',
      description: 'Arama motorlarında üst sıralarda yer alın. SEO stratejisi, teknik optimizasyon ve içerik planlama.',
      descriptionEn: 'Rank higher in search engines. SEO strategy, technical optimization and content planning.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'SEO audit ve analiz',
        'Teknik SEO optimizasyonu',
        'İçerik stratejisi',
        'Anahtar kelime araştırması',
        'Backlink stratejisi',
        'Site hızı optimizasyonu',
        'Mobil uyumluluk',
        'Local SEO optimizasyonu'
      ],
      featuresEn: [
        'SEO audit and analysis',
        'Technical SEO optimization',
        'Content strategy',
        'Keyword research',
        'Backlink strategy',
        'Site speed optimization',
        'Mobile compatibility',
        'Local SEO optimization'
      ],
      category: 'SEO & GEO',
      categoryEn: 'SEO & GEO',
      icon: 'Search'
    },
    {
      id: 'web-design-consulting',
      name: 'Web Tasarım Danışmanlığı',
      nameEn: 'Web Design Consulting',
      description: 'Modern ve kullanıcı dostu web siteleri için tasarım danışmanlığı. UX/UI optimizasyonu ve dönüşüm artırma.',
      descriptionEn: 'Design consulting for modern and user-friendly websites. UX/UI optimization and conversion improvement.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'UX/UI değerlendirme',
        'Dönüşüm optimizasyonu',
        'Responsive tasarım önerileri',
        'Kullanıcı deneyimi analizi',
        'Tasarım best practices',
        'A/B test planlaması',
        'Mobil optimizasyon',
        'Erişilebilirlik iyileştirme'
      ],
      featuresEn: [
        'UX/UI evaluation',
        'Conversion optimization',
        'Responsive design suggestions',
        'User experience analysis',
        'Design best practices',
        'A/B test planning',
        'Mobile optimization',
        'Accessibility improvement'
      ],
      category: 'Web Tasarımı',
      categoryEn: 'Web Design',
      icon: 'Globe'
    },
    {
      id: 'ecommerce-consulting',
      name: 'E-Ticaret Danışmanlığı',
      nameEn: 'E-commerce Consulting',
      description: 'E-ticaret işinizi büyütün. Satış stratejileri, platform optimizasyonu ve müşteri deneyimi iyileştirme.',
      descriptionEn: 'Grow your e-commerce business. Sales strategies, platform optimization and customer experience improvement.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'E-ticaret stratejisi',
        'Ürün katalog optimizasyonu',
        'Checkout süreci iyileştirme',
        'Müşteri segmentasyonu',
        'Pazarlama otomasyonu',
        'Stok yönetimi',
        'Kargo entegrasyonu',
        'Müşteri hizmetleri optimizasyonu'
      ],
      featuresEn: [
        'E-commerce strategy',
        'Product catalog optimization',
        'Checkout process improvement',
        'Customer segmentation',
        'Marketing automation',
        'Inventory management',
        'Shipping integration',
        'Customer service optimization'
      ],
      category: 'E-Ticaret',
      categoryEn: 'E-commerce',
      icon: 'ShoppingCart'
    },
    {
      id: 'social-media-consulting',
      name: 'Sosyal Medya Danışmanlığı',
      nameEn: 'Social Media Consulting',
      description: 'Sosyal medya stratejinizi güçlendirin. İçerik planlama, topluluk yönetimi ve marka bilinirliği artırma.',
      descriptionEn: 'Strengthen your social media strategy. Content planning, community management and brand awareness increase.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'İçerik stratejisi',
        'Topluluk yönetimi',
        'Influencer işbirlikleri',
        'Sosyal medya analitiği',
        'Kriz yönetimi',
        'Hashtag stratejileri',
        'Story ve Reels optimizasyonu',
        'Community building'
      ],
      featuresEn: [
        'Content strategy',
        'Community management',
        'Influencer collaborations',
        'Social media analytics',
        'Crisis management',
        'Hashtag strategies',
        'Story and Reels optimization',
        'Community building'
      ],
      category: 'Sosyal Medya',
      categoryEn: 'Social Media',
      icon: 'Users'
    },
    {
      id: 'analytics-consulting',
      name: 'Analitik & Raporlama Danışmanlığı',
      nameEn: 'Analytics & Reporting Consulting',
      description: 'Veri odaklı kararlar alın. Google Analytics, veri analizi ve performans raporlama danışmanlığı.',
      descriptionEn: 'Make data-driven decisions. Google Analytics, data analysis and performance reporting consulting.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'Google Analytics kurulumu',
        'Veri analizi ve yorumlama',
        'Dashboard oluşturma',
        'Performans metrikleri',
        'Raporlama stratejileri',
        'Conversion tracking',
        'Event tracking',
        'Custom dimensions'
      ],
      featuresEn: [
        'Google Analytics setup',
        'Data analysis and interpretation',
        'Dashboard creation',
        'Performance metrics',
        'Reporting strategies',
        'Conversion tracking',
        'Event tracking',
        'Custom dimensions'
      ],
      category: 'Analitik',
      categoryEn: 'Analytics',
      icon: 'BarChart3'
    },
    {
      id: 'general-marketing-consulting',
      name: 'Genel Dijital Pazarlama Danışmanlığı',
      nameEn: 'General Digital Marketing Consulting',
      description: 'Dijital pazarlama stratejinizi tüm yönleriyle değerlendirin. Kapsamlı pazarlama danışmanlığı ve yol haritası.',
      descriptionEn: 'Evaluate your digital marketing strategy comprehensively. Comprehensive marketing consulting and roadmap.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'Pazarlama stratejisi',
        'Kanal analizi',
        'Bütçe planlama',
        'ROI optimizasyonu',
        'Kapsamlı raporlama',
        'Rekabet analizi',
        'Marka konumlandırma',
        'Hedef kitle tanımlama'
      ],
      featuresEn: [
        'Marketing strategy',
        'Channel analysis',
        'Budget planning',
        'ROI optimization',
        'Comprehensive reporting',
        'Competitive analysis',
        'Brand positioning',
        'Target audience definition'
      ],
      category: 'Genel',
      categoryEn: 'General',
      icon: 'TrendingUp'
    }
  ]

  const packageItem = consultingPackages.find(pkg => pkg.id === id)

  if (!packageItem) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {language === 'tr' ? 'Paket bulunamadı' : 'Package not found'}
          </h1>
          <Link to="/consulting" className="text-blue-600 hover:text-blue-700">
            {language === 'tr' ? 'Danışmanlık sayfasına dön' : 'Back to consulting page'}
          </Link>
        </div>
      </div>
    )
  }

  const getIcon = (iconName: string) => {
    const icons: Record<string, any> = {
      TrendingUp,
      Users,
      Search,
      Globe,
      ShoppingCart,
      BarChart3
    }
    return icons[iconName] || TrendingUp
  }

  const IconComponent = getIcon(packageItem.icon || 'TrendingUp')

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(packageItem, 1)
    }
    setIsCartOpen(true)
  }

  const totalPrice = packageItem.totalPrice * quantity

  return (
    <>
      <Helmet>
        <title>{language === 'tr' ? packageItem.name : packageItem.nameEn} - Thinklyze</title>
        <meta name="description" content={language === 'tr' ? packageItem.description : packageItem.descriptionEn} />
      </Helmet>

      <div className="bg-[#f9f9f9]">
        {/* Hero Section */}
        <section className="relative bg-[#f9f9f9] py-16 md:py-24 overflow-hidden">
          {/* Grid Background */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
              backgroundSize: '64px 64px',
              maskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%, #000 70%, transparent 100%)'
            }}
          />
          
          <div className="max-w-7xl mx-auto px-6 relative">
            <Link
              to="/consulting"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'tr' ? 'Danışmanlık sayfasına dön' : 'Back to consulting'}
            </Link>

            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Left Column - Package Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center bg-white border border-gray-200 px-3 py-1.5 rounded-lg text-sm font-medium mb-6">
                  <IconComponent className="w-4 h-4 mr-2 text-blue-600" />
                  {language === 'tr' ? packageItem.category : packageItem.categoryEn}
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  {language === 'tr' ? packageItem.name : packageItem.nameEn}
                </h1>
                
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {language === 'tr' ? packageItem.description : packageItem.descriptionEn}
                </p>

                {/* Features Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {(language === 'tr' ? packageItem.features : packageItem.featuresEn).slice(0, 6).map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-2"
                    >
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right Column - Price Card (Sticky) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:sticky lg:top-24"
              >
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                  {/* Price Header */}
                  <div className="text-center mb-6 pb-5 border-b border-gray-100">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 mb-3">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-1">
                      {packageItem.totalPrice.toLocaleString('tr-TR')} ₺
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      {language === 'tr' ? '1 Saatlik Danışmanlık' : '1 Hour Consulting'}
                    </div>
                  </div>

                  {/* Quantity Selector */}
                  <div className="mb-5">
                    <label className="text-xs font-medium text-gray-700 mb-2 block">
                      {language === 'tr' ? 'Seans Sayısı:' : 'Number of Sessions:'}
                    </label>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-gray-700 hover:text-blue-600 font-semibold text-lg"
                      >
                        −
                      </button>
                      <div className="w-16 h-10 flex items-center justify-center rounded-lg border-2 border-gray-200 bg-gray-50">
                        <span className="text-lg font-bold text-gray-900">{quantity}</span>
                      </div>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-gray-700 hover:text-blue-600 font-semibold text-lg"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Total Price */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-medium text-gray-700 block">
                          {language === 'tr' ? 'Toplam Tutar:' : 'Total Amount:'}
                        </span>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          {language === 'tr' ? 'KDV dahil' : 'VAT included'}
                        </p>
                      </div>
                      <span className="text-2xl font-bold text-blue-600">
                        {totalPrice.toLocaleString('tr-TR')} ₺
                      </span>
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    onClick={handleAddToCart}
                    className="w-full px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold text-base shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 group mb-4"
                  >
                    <ShoppingCart className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    {language === 'tr' ? 'Sepete Ekle' : 'Add to Cart'}
                  </button>

                  {/* Trust Indicators */}
                  <div className="pt-4 border-t border-gray-100 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{language === 'tr' ? 'Güvenli ödeme' : 'Secure payment'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{language === 'tr' ? 'Uzman danışmanlar' : 'Expert consultants'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{language === 'tr' ? 'Detaylı rapor' : 'Detailed report'}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section - Full List */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {language === 'tr' ? 'Detaylı Hizmet İçeriği' : 'Detailed Service Content'}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {language === 'tr' 
                  ? '1 saatlik danışmanlık seansında ele alınacak tüm konular'
                  : 'All topics covered in the 1-hour consultation session'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(language === 'tr' ? packageItem.features : packageItem.featuresEn).map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-200 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center flex-shrink-0 group-hover:from-green-100 group-hover:to-green-200 transition-colors">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {feature}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What to Expect Section */}
        <section className="py-20 md:py-28 bg-[#f9f9f9]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {language === 'tr' ? 'Süreç Nasıl İşler?' : 'How Does It Work?'}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {language === 'tr' 
                  ? 'Danışmanlık sürecinin adım adım işleyişi'
                  : 'Step-by-step process of the consultation'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full">
                  <div className="absolute -top-4 left-8 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {language === 'tr' ? 'Ön Hazırlık' : 'Pre-preparation'}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {language === 'tr'
                        ? 'Danışmanlık öncesi mevcut durumunuzu analiz etmeniz için bir kontrol listesi gönderilir. Bu sayede seanstan maksimum verim alırsınız.'
                        : 'A checklist is sent before consultation to analyze your current situation. This ensures maximum benefit from the session.'}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full">
                  <div className="absolute -top-4 left-8 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {language === 'tr' ? 'Danışmanlık Seansı' : 'Consultation Session'}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {language === 'tr'
                        ? 'Video konferans veya yüz yüze görüşme ile 1 saatlik detaylı danışmanlık seansı gerçekleştirilir. Sorularınız yanıtlanır ve stratejiler belirlenir.'
                        : 'A detailed 1-hour consultation session is conducted via video conference or face-to-face. Your questions are answered and strategies are defined.'}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full">
                  <div className="absolute -top-4 left-8 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {language === 'tr' ? 'Rapor ve Takip' : 'Report and Follow-up'}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {language === 'tr'
                        ? 'Seans sonrası detaylı bir rapor ve uygulanabilir öneriler e-posta ile gönderilir. 7 gün boyunca e-posta ile destek sağlanır.'
                        : 'A detailed report and actionable recommendations are sent via email after the session. Email support is provided for 7 days.'}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

      {/* Cart Modal */}
      <CartModal 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)}
      />
    </>
  )
}

export default ConsultingDetail















